import { type Dispatch, type FC, type SetStateAction, useLayoutEffect, useState } from "react"
import styled from "styled-components"

import { type Membership } from "@forento/core/models/membership"
import { type UseFile, useFile } from "@forento/ui/hooks/useFile"

import Button, { SubmitButton } from "~/components/Button"
import Checkbox from "~/components/Checkbox"
import { CalendarIcon, CourseIcon, DownloadIcon, SessionsIcon, VideoIcon } from "~/components/Icon"
import ImageUploader from "~/components/ImageUploader"
import InputField, { useRichTextArea } from "~/components/InputField"
import Link from "~/components/Link"
import { DefaultModal, ModalButtons, ModalContentPadding, ModalTitle } from "~/components/Modal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { useAlert } from "~/contexts/AlertContext"
import { usePlatform } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

type Data = {
	title: string
	shortDescription: string
	isMembersLimited: boolean
	maxMembers: string
	monthlyPricing: { isEnabled: boolean; value: number }
	annuallyPricing: { isEnabled: boolean; value: number }
}

type Props = { membershipId: number | null; isOpen: boolean; onClose(): void; onSubmitted(): Promise<unknown> }
const EditModal: FC<Props> = ({ membershipId, isOpen, onClose, onSubmitted }) => {
	const alert = useAlert()

	const enabled = isOpen && membershipId !== null
	const { data: membership, error, refetch } = query.membership.get.useQuery(membershipId ?? -1, { enabled })

	const [data, setData] = useState<Data>({
		title: "",
		shortDescription: "",
		isMembersLimited: false,
		maxMembers: "10",
		monthlyPricing: { isEnabled: false, value: 0 },
		annuallyPricing: { isEnabled: false, value: 0 },
	})
	const longDescription = useRichTextArea({ label: "Description", initialValue: membership?.longDescription })
	const [isSubmitting, setSubmitting] = useState(false)

	const thumbnail = useFile(membership?.id ?? -1, membership?.thumbnailFilePath ?? null)

	useLayoutEffect(() => {
		setData({
			title: membership?.title ?? "",
			shortDescription: membership?.shortDescription ?? "",
			isMembersLimited: membership?.maxMembers !== null,
			maxMembers: membership?.maxMembers?.toString() ?? "10",
			monthlyPricing: {
				isEnabled: membership?.prices.find(x => x.paymentInterval === "month") !== undefined,
				value: membership?.prices.find(x => x.paymentInterval === "month")?.amount ?? 0,
			},
			annuallyPricing: {
				isEnabled: membership?.prices.find(x => x.paymentInterval === "year") !== undefined,
				value: membership?.prices.find(x => x.paymentInterval === "year")?.amount ?? 0,
			},
		})
	}, [membership])

	async function handleSubmit() {
		const maxMembers = data.isMembersLimited ? Number(data.maxMembers) : null
		if (!membership) return

		if (data.title.length === 0) {
			alert.show("Error", "Please fill in the title.")
			return
		}
		if (maxMembers !== null && (Number.isNaN(maxMembers) || maxMembers < 0)) {
			alert.show("Error", "Please fill in a valid number of max members.")
			return
		}
		if (!data.monthlyPricing.isEnabled && !data.annuallyPricing.isEnabled) {
			alert.show("Error", "You need at least one pricing plan.")
			return
		}
		if (
			(data.monthlyPricing.isEnabled && data.monthlyPricing.value <= 0) ||
			(data.annuallyPricing.isEnabled && data.annuallyPricing.value <= 0)
		) {
			alert.show(
				"Free membership",
				'You don\'t need to create any free membership tier. If you want to offer free content to users of your platform, you can publish products as "Free" or "Signed in". This will allow your users to access content without going through any sort of payment gateway checkout. You can learn more here: [🛒 Products on Forento](https://forento.link/membership-modal-products) & [📦 Memberships](https://forento.link/membership-modal-memberships).',
			)
			return
		}
		if (
			data.monthlyPricing.isEnabled &&
			data.annuallyPricing.isEnabled &&
			data.annuallyPricing.value > data.monthlyPricing.value * 12
		) {
			const dialog = await alert.confirm(
				"Price mismatch",
				"Your annual plan is more expensive than your monthly plan. Are you sure this is intentional?",
				{ confirmLabel: "Continue", isDestructive: false },
			)
			if (!dialog.result) return
			dialog.close()
		}

		if (membership.prices.some(x => x.paymentInterval === "month") && !data.monthlyPricing.isEnabled) {
			const dialog = await alert.confirm(
				"Delete monthly plan",
				"You have deleted the monthly plan. If this has current subscribers they will be immediately cancelled. Are you sure you want to delete it?",
			)
			if (!dialog.result) return
			dialog.close()
		}
		if (membership.prices.some(x => x.paymentInterval === "year") && !data.annuallyPricing.isEnabled) {
			const dialog = await alert.confirm(
				"Delete annual plan",
				"You have deleted the annual plan. If this has current subscribers they will be immediately cancelled. Are you sure you want to delete it?",
			)
			if (!dialog.result) return
			dialog.close()
		}

		setSubmitting(true)

		try {
			await trpc.membership.update.mutate({
				id: membership.id,
				data: {
					title: data.title,
					shortDescription: data.shortDescription,
					longDescription: await longDescription.exportEditorState(),
					thumbnailDataUrl: await thumbnail.exportToDataUrl(),
					maxMembers,
					price: {
						month: data.monthlyPricing.isEnabled ? data.monthlyPricing.value : null,
						year: data.annuallyPricing.isEnabled ? data.annuallyPricing.value : null,
					},
				},
			})

			await Promise.all([refetch(), onSubmitted()])
			onClose()
		} catch (error) {
			console.error(error)
			setSubmitting(false)
		}
		setSubmitting(false)
	}

	return (
		<DefaultModal onClose={onClose} isOpen={isOpen} width={800} onSubmit={handleSubmit}>
			{error ? (
				<p>Failed to load membership.</p>
			) : !membership ? (
				<PartialLoadingPage />
			) : (
				<Content
					membership={membership}
					data={data}
					setData={setData}
					longDescription={longDescription}
					thumbnail={thumbnail}
					isSubmitting={isSubmitting}
					onClose={onClose}
					onSubmitted={onSubmitted}
				/>
			)}
		</DefaultModal>
	)
}

type ContentProps = {
	membership: Membership
	data: Data
	setData: Dispatch<SetStateAction<Data>>
	longDescription: ReturnType<typeof useRichTextArea>
	thumbnail: UseFile
	isSubmitting: boolean
	onClose(): void
	onSubmitted(): Promise<unknown>
}
const Content: FC<ContentProps> = ({
	membership,
	data,
	setData,
	longDescription,
	thumbnail,
	isSubmitting,
	onClose,
	onSubmitted,
}) => {
	const platform = usePlatform()!
	const alert = useAlert()

	function setField<T>(field: keyof Data): (value: T) => void {
		return value => setData(current => ({ ...current, [field]: value }))
	}

	async function handleDelete() {
		const dialog = await alert.confirm(
			"Delete membership tier",
			"Are you sure you want to delete this membership tier?",
		)
		if (!dialog.result) return

		const response = await trpc.membership.delete.mutate(membership.id)
		switch (response.status) {
			case "has-subscribers":
				return alert.show(
					"Failed to delete membership",
					"Could not delete membership tier because it has active subscribers.",
				)
		}

		await onSubmitted()
		dialog.close()
		onClose()
	}

	return (
		<>
			<ModalTitle>
				Edit tier <strong>{membership.title}</strong>
			</ModalTitle>
			<ModalContentPadding>
				<Section>
					<SectionTitle>Basic information</SectionTitle>
					<InputField label="Name" value={data.title} onChange={setField("title")} />
					<InputField
						label="Short description"
						value={data.shortDescription}
						onChange={setField("shortDescription")}
					/>
					{longDescription.element}
					<ImageUploader label="Thumbnail" recommendedSize="thumbnail" {...thumbnail.imageUploader} />
				</Section>

				<Section>
					<SectionTitle>Member limits</SectionTitle>
					<Checkbox
						label="Limit the number of members"
						isChecked={data.isMembersLimited}
						onChange={value => setField("isMembersLimited")(value)}
					/>
					{data.isMembersLimited && (
						<InputField
							label="Max members"
							inputType="number"
							value={data.maxMembers}
							onChange={setField("maxMembers")}
						/>
					)}
				</Section>

				<Section>
					<SectionTitle>Pricing plans</SectionTitle>
					<PricingContainer>
						<PricingCard>
							<Checkbox
								label="Offer monthly plan"
								isChecked={data.monthlyPricing.isEnabled}
								onChange={value =>
									setField("monthlyPricing")({ ...data.monthlyPricing, isEnabled: value })
								}
								className="mb-3"
							/>
							<InputField
								label={`Price per month (${platform.currency})`}
								inputType="number"
								value={data.monthlyPricing.value.toString()}
								onChange={value =>
									setField("monthlyPricing")({ ...data.monthlyPricing, value: Number(value) })
								}
								disabled={!data.monthlyPricing.isEnabled}
							/>
						</PricingCard>
						<PricingCard>
							<Checkbox
								label="Offer annual plan"
								isChecked={data.annuallyPricing.isEnabled}
								onChange={value =>
									setField("annuallyPricing")({ ...data.annuallyPricing, isEnabled: value })
								}
								className="mb-3"
							/>
							<InputField
								label={`Price per year (${platform.currency})`}
								inputType="number"
								value={data.annuallyPricing.value.toString()}
								onChange={value =>
									setField("annuallyPricing")({ ...data.annuallyPricing, value: Number(value) })
								}
								disabled={!data.annuallyPricing.isEnabled}
							/>
						</PricingCard>
					</PricingContainer>
					<PricingNote>Changing the price does not affect existing members.</PricingNote>
				</Section>

				<Section>
					<SectionTitle>Included content</SectionTitle>
					{membership.content.length === 0 ? (
						<EmptyContentState>
							<div className="mb-4 text-5xl">📚</div>
							<h3 className="mb-2 text-lg font-semibold">No content included</h3>
							<p className="text-light text-sm">Add items from the product's pricing page</p>
						</EmptyContentState>
					) : (
						<ContentList>
							{membership.content.map(product => (
								<ContentItem key={`${product.type}-${product.id}`} {...product} />
							))}
						</ContentList>
					)}
				</Section>
			</ModalContentPadding>
			<ModalButtons>
				<Button variant="primary-danger" onClick={handleDelete}>
					Delete
				</Button>
				<Button variant="secondary" onClick={onClose}>
					Cancel
				</Button>
				<SubmitButton variant="primary" isLoading={isSubmitting}>
					Update tier
				</SubmitButton>
			</ModalButtons>
		</>
	)
}

const contentIcons = {
	course: CourseIcon,
	video: VideoIcon,
	downloadable: DownloadIcon,
	event: CalendarIcon,
	session: SessionsIcon,
} as const

const contentLinks = {
	course: routes.course.settings,
	video: routes.video.edit,
	downloadable: routes.downloadable.edit,
	event: routes.main.event,
	session: routes.sessions.edit,
} as const

type ContentItemProps = { id: number; type: "course" | "video" | "downloadable" | "event" | "session"; title: string }
const ContentItem: FC<ContentItemProps> = ({ id, type, title }) => {
	const Icon = contentIcons[type]

	return (
		<StyledContentItem>
			<ContentIcon>
				<Icon className="text-primary size-6" />
			</ContentIcon>
			<ContentTitle>{title}</ContentTitle>
			<ContentLink
				to={contentLinks[type](id)}
				className="text-primary hover:bg-primary/10 rounded-md px-3 py-2 text-sm font-medium transition-colors"
			>
				Manage in product settings
			</ContentLink>
		</StyledContentItem>
	)
}

const Section = styled.div`
	margin-bottom: 32px;
	padding-bottom: 24px;
	border-bottom: 1px solid #f0f0f0;
	display: flex;
	flex-direction: column;
	gap: 16px;

	&:last-child {
		margin-bottom: 0;
		padding-bottom: 0;
		border-bottom: none;
	}
`

const SectionTitle = styled.h3`
	font-weight: 600;
	font-size: 16px;
	color: #1f2937;
	display: flex;
	align-items: center;
	gap: 8px;

	&::before {
		content: "";
		width: 4px;
		height: 16px;
		background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
		border-radius: 2px;
	}
`

const PricingContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 20px;
	margin-bottom: 12px;

	@media (max-width: 640px) {
		grid-template-columns: 1fr;
		gap: 16px;
	}
`

const PricingCard = styled.div`
	background: #f8fafc;
	border: 1px solid #e2e8f0;
	border-radius: 12px;
	padding: 20px;
	transition: all 0.2s ease;

	&:hover {
		border-color: #cbd5e1;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	}
`

const PricingNote = styled.p`
	text-align: center;
	font-size: 13px;
	color: #64748b;
	background: #f1f5f9;
	border-radius: 8px;
	padding: 12px;
	margin: 0;
`

const EmptyContentState = styled.div`
	background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
	border: 2px dashed #cbd5e1;
	border-radius: 16px;
	padding: 40px 24px;
	text-align: center;
	transition: all 0.2s ease;

	&:hover {
		border-color: #94a3b8;
		background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
	}
`

const ContentList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`

const StyledContentItem = styled.div`
	background: white;
	border: 1px solid #e2e8f0;
	border-radius: 12px;
	padding: 16px;
	display: flex;
	align-items: center;
	gap: 12px;
	transition: all 0.2s ease;

	&:hover {
		border-color: #cbd5e1;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
		transform: translateY(-1px);
	}
`

const ContentIcon = styled.div`
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	border-radius: 10px;
	flex-shrink: 0;
`

const ContentTitle = styled.h3`
	flex: 1;
	font-weight: 600;
	font-size: 14px;
	color: #1f2937;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	margin: 0;
`

const ContentLink = styled(Link)`
	text-decoration: none;
	flex-shrink: 0;
`

export default EditModal
